

import { useQuery } from "@tanstack/react-query";
import type { StoreSubscriptionPaymentView } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { queryKeys } from "@/lib/query/core/query-keys";

type UseStoreSubscriptionPaymentsQueryParams = {
  storeId: string;
  includeDeleted?: boolean;
};

type NormalizedFilters = {
  includeDeleted?: true;
};

function normalizeFilters({
  includeDeleted,
}: Omit<UseStoreSubscriptionPaymentsQueryParams, "storeId">): NormalizedFilters {
  return {
    includeDeleted: includeDeleted ? true : undefined,
  };
}

function buildPath(storeId: string, filters: NormalizedFilters) {
  const query = new URLSearchParams();
  if (filters.includeDeleted) {
    query.set("includeDeleted", "true");
  }

  const queryString = query.toString();
  const path = endpoints.admin.storeSubscriptionPayments(storeId);
  return queryString.length > 0 ? `${path}?${queryString}` : path;
}

export function useStoreSubscriptionPaymentsQuery(
  params: UseStoreSubscriptionPaymentsQueryParams,
) {
  const { storeId, ...filters } = params;
  const normalizedFilters = normalizeFilters(filters);

  const query = useQuery({
    queryKey: queryKeys.admin.storeSubscriptionPayments(storeId, normalizedFilters),
    queryFn: () =>
      apiJsonClient<StoreSubscriptionPaymentView[]>(
        buildPath(storeId, normalizedFilters),
      ),
    enabled: storeId.length > 0,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
